'use client';

import React, { useState, useEffect, useRef } from 'react';
import { colors, baseStyles, formatUSD, formatNum } from './SharedUI';

const formatSmartPrice = (val) => {
  if (!val) return '$0';
  const num = parseFloat(val);
  if (num >= 1000) return `$${formatNum(num, 2)}`;
  if (num >= 1) return `$${formatNum(num, 4)}`;
  if (num >= 0.001) return `$${formatNum(num, 6)}`;
  return `$${formatNum(num, 8)}`;
};

export default function MarketScreener({ onOpenRadar }) {
  // ==========================================
  // STATE BƯỚC 1: Lọc Volume 
  // ==========================================
  const [minVolM, setMinVolM] = useState(10);   
  const [maxVolM, setMaxVolM] = useState(1000); 
  const [step1Loading, setStep1Loading] = useState(false);
  
  // ==========================================
  // STATE BƯỚC 2: Watchlist & Lọc thủ công
  // ==========================================
  const [rawCoinList, setRawCoinList] = useState([]); 
  const [coinList, setCoinList] = useState([]);       
  const [selectedCoins, setSelectedCoins] = useState(new Set());
  
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minPct, setMinPct] = useState('');
  const [maxPct, setMaxPct] = useState('');

  // ==========================================
  // STATE BƯỚC 3: Cấu hình MA Trigger & Auto
  // ==========================================
  const [maInterval, setMaInterval] = useState('1h');
  const [gapPct, setGapPct] = useState(0.5);
  
  const [filterMABelow, setFilterMABelow] = useState(false); 
  const [filterMAAbove, setFilterMAAbove] = useState(false); 

  const [filterLong, setFilterLong] = useState(false);  
  const [filterShort, setFilterShort] = useState(false); 

  const [enableRangeFilter, setEnableRangeFilter] = useState(false);
  const [rangeLookback, setRangeLookback] = useState(10); 
  const [rangeThreshold, setRangeThreshold] = useState(10); 

  const [pendingAuto, setPendingAuto] = useState(false); 
  const [autoTime, setAutoTime] = useState(15); 
  const [isAuto, setIsAuto] = useState(false);           
  const [activeInterval, setActiveInterval] = useState(15); 

  const [step3Loading, setStep3Loading] = useState(false);
  const isScanningRef = useRef(false); 
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  
  const [scanResults, setScanResults] = useState([]);

  const fetchMarketOverview = async () => {
    setStep1Loading(true);
    setRawCoinList([]);
    setCoinList([]);
    setScanResults([]);
    try {
      const exRes = await fetch('https://fapi.binance.com/fapi/v1/exchangeInfo');
      const exInfo = await exRes.json();
      const validSymbols = new Set(
        exInfo.symbols
          .filter(s => s.quoteAsset === 'USDT' && s.status === 'TRADING' && s.contractType === 'PERPETUAL')
          .map(s => s.symbol)
      );
      const tkRes = await fetch('https://fapi.binance.com/fapi/v1/ticker/24hr');
      const tkData = await tkRes.json();
      const actualMinVol = parseFloat(minVolM) * 1000000;
      const actualMaxVol = parseFloat(maxVolM) * 1000000;
      const filtered = tkData
        .filter(t => validSymbols.has(t.symbol))
        .map(t => ({
          symbol: t.symbol,
          lastPrice: parseFloat(t.lastPrice),
          priceChange: parseFloat(t.priceChangePercent),
          quoteVolume: parseFloat(t.quoteVolume)
        }))
        .filter(t => t.quoteVolume >= actualMinVol && t.quoteVolume <= actualMaxVol)
        .sort((a, b) => b.quoteVolume - a.quoteVolume);
      setRawCoinList(filtered); 
      setCoinList(filtered);
      setSelectedCoins(new Set(filtered.map(c => c.symbol))); 
    } catch (error) {
      console.error("Lỗi Quét Thị Trường:", error);
      alert("Lỗi kết nối API Binance!");
    } finally {
      setStep1Loading(false);
    }
  };

  const handleStep2Filter = () => {
    let temp = [...rawCoinList]; 
    if (minPrice !== '') temp = temp.filter(c => c.lastPrice >= parseFloat(minPrice));
    if (maxPrice !== '') temp = temp.filter(c => c.lastPrice <= parseFloat(maxPrice));
    if (minPct !== '') temp = temp.filter(c => c.priceChange >= parseFloat(minPct));
    if (maxPct !== '') temp = temp.filter(c => c.priceChange <= parseFloat(maxPct));
    setCoinList(temp);
    setSelectedCoins(new Set(temp.map(c => c.symbol))); 
  };

  const toggleSelectAll = () => {
    if (selectedCoins.size === coinList.length) setSelectedCoins(new Set()); 
    else setSelectedCoins(new Set(coinList.map(c => c.symbol))); 
  };

  const toggleCoin = (symbol) => {
    const newSet = new Set(selectedCoins);
    if (newSet.has(symbol)) newSet.delete(symbol);
    else newSet.add(symbol);
    setSelectedCoins(newSet);
  };

  const handleCheckMABelow = (e) => {
    setFilterMABelow(e.target.checked);
    if (e.target.checked) setFilterMAAbove(false);
  };

  const handleCheckMAAbove = (e) => {
    setFilterMAAbove(e.target.checked);
    if (e.target.checked) setFilterMABelow(false);
  };

  const handleCheckLong = (e) => {
    setFilterLong(e.target.checked);
    if (e.target.checked) setFilterShort(false);
  };

  const handleCheckShort = (e) => {
    setFilterShort(e.target.checked);
    if (e.target.checked) setFilterLong(false);
  };

  const handleSaveMode = () => {
    setIsAuto(pendingAuto);
    setActiveInterval(autoTime); 
    if (pendingAuto && !isScanningRef.current) {
      scanMACross();
    }
  };

  const scanMACross = async () => {
    if (selectedCoins.size === 0) {
      if (!isAuto) alert("Vui lòng chọn ít nhất 1 đồng Coin ở Bước 2!");
      return;
    }
    if (isScanningRef.current) return;
    isScanningRef.current = true;
    setStep3Loading(true);
    setScanResults([]); 
    const targets = Array.from(selectedCoins);
    setProgress({ current: 0, total: targets.length });
    const bufferResults = [];

    for (let i = 0; i < targets.length; i++) {
      const symbol = targets[i];
      setProgress({ current: i + 1, total: targets.length });
      try {
        const res = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${symbol}&interval=${maInterval}&limit=150`);
        const klines = await res.json();
        if (klines && klines.length >= 99) {
          const closes = klines.map(k => parseFloat(k[4]));
          const livePrice = closes[closes.length - 1];
          
          const ma7 = closes.slice(-7).reduce((a, b) => a + b, 0) / 7;
          const ma25 = closes.slice(-25).reduce((a, b) => a + b, 0) / 25;
          const ma99 = closes.slice(-99).reduce((a, b) => a + b, 0) / 99; 
          
          const gap = (Math.abs(ma7 - ma25) / ma25) * 100;
          const gap99 = ((ma7 - ma99) / ma99) * 100; 

          const priceMa7Gap = ((livePrice - ma7) / ma7) * 100;
          const priceMa25Gap = ((livePrice - ma25) / ma25) * 100; // Thêm lệch Giá vs MA25

          if (enableRangeFilter) {
            let hasBigCandle = false;
            const lookback = parseInt(rangeLookback);
            const threshold = parseFloat(rangeThreshold);
            for (let j = 0; j < lookback; j++) {
              const idx = klines.length - 1 - j;
              if (idx < 0) break;
              const h = parseFloat(klines[idx][2]);
              const l = parseFloat(klines[idx][3]);
              if (((h - l) / l) * 100 >= threshold) {
                hasBigCandle = true;
                break;
              }
            }
            if (!hasBigCandle) continue;
          }

          if (gap <= gapPct) {
            if (filterMABelow && ma7 >= ma25) continue; 
            if (filterMAAbove && ma7 < ma25) continue;  
            if (filterLong && livePrice <= ma7) continue;   
            if (filterShort && livePrice >= ma7) continue;  

            const coinInfo = rawCoinList.find(c => c.symbol === symbol) || {};
            const isGolden = ma7 >= ma25;

            bufferResults.push({
              symbol, livePrice, ma7, ma25, ma99, gap, gap99, priceMa7Gap, priceMa25Gap,
              vol: coinInfo.quoteVolume || 0,
              change: coinInfo.priceChange || 0,
              status: isGolden ? "ĐÃ CẮT LÊN (GOLDEN)" : (ma7 < ma25 ? "SẮP CẮT LÊN (LONG)" : "SẮP CẮT XUỐNG (SHORT)"),
              color: isGolden ? colors.warn : (ma7 < ma25 ? colors.green : colors.red)
            });
            setScanResults([...bufferResults]); 
          }
        }
        await new Promise(r => setTimeout(r, 100)); 
      } catch (e) { console.error(`Lỗi MA ${symbol}`, e); }
    }
    setStep3Loading(false);
    isScanningRef.current = false;
  };

  const scanFnRef = useRef(scanMACross);
  useEffect(() => { scanFnRef.current = scanMACross; }, [scanMACross]);

  useEffect(() => {
    let intervalId;
    if (isAuto) {
      intervalId = setInterval(() => { scanFnRef.current(); }, activeInterval * 60 * 1000); 
    }
    return () => { if (intervalId) clearInterval(intervalId); }
  }, [isAuto, activeInterval]);

  return (
    <div style={{ ...baseStyles.card, marginBottom: '24px' }}>
      <div style={{ borderBottom: `1px solid ${colors.border}`, paddingBottom: '12px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <strong style={{ color: colors.textMain, fontSize: '16px' }}>📡 RADAR TẦM XA: GIAO CẮT MA TOÀN THỊ TRƯỜNG</strong>
        {isAuto && (
          <div style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', border: `1px solid ${colors.green}`, color: colors.green, padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '6px', height: '6px', backgroundColor: colors.green, borderRadius: '50%', animation: 'pulse 1.5s infinite' }}></div>
            HỆ THỐNG ĐANG AUTO QUÉT ({activeInterval}p/lần)
          </div>
        )}
      </div>

      <div style={{ padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', marginBottom: '16px', border: `1px solid ${colors.border}` }}>
        <div style={{ ...colors.textSub, fontSize: '13px', marginBottom: '12px', fontWeight: 'bold' }}>🛑 BƯỚC 1: LỌC NHIỄU BẰNG VOLUME (API 24hr)</div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>Min Vol 24h (Triệu $)</div>
            <input type="number" value={minVolM} onChange={e => setMinVolM(e.target.value)} style={{ ...baseStyles.input, maxWidth: '140px' }} disabled={isAuto} />
          </div>
          <div>
            <div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>Max Vol 24h (Triệu $)</div>
            <input type="number" value={maxVolM} onChange={e => setMaxVolM(e.target.value)} style={{ ...baseStyles.input, maxWidth: '140px' }} disabled={isAuto} />
          </div>
          <button onClick={fetchMarketOverview} disabled={step1Loading || isAuto} style={{ ...baseStyles.select, backgroundColor: step1Loading || isAuto ? colors.border : '#3b82f6', color: '#fff', border: 'none', fontWeight: 'bold' }}>
            {step1Loading ? '⏳ ĐANG QUÉT...' : '⚡ QUÉT TỔNG THỂ 300+ COIN'}
          </button>
        </div>
      </div>

      {rawCoinList.length > 0 && (
        <div style={{ padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', marginBottom: '16px', border: `1px solid ${colors.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center' }}>
            <div style={{ fontSize: '13px', color: colors.textMain, fontWeight: 'bold' }}>🎯 BƯỚC 2: WATCHLIST & LỌC ĐIỀU KIỆN <span style={{ color: colors.green, fontWeight: 'normal', marginLeft: '8px' }}>(Còn lại: {coinList.length} Coin)</span></div>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px', border: `1px solid ${colors.border}` }}>
            <div><div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>Giá Min ($)</div><input type="number" step="0.0001" value={minPrice} onChange={e => setMinPrice(e.target.value)} style={{ ...baseStyles.input, maxWidth: '100px', fontSize: '12px' }} disabled={isAuto} /></div>
            <div><div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>Giá Max ($)</div><input type="number" step="0.0001" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} style={{ ...baseStyles.input, maxWidth: '100px', fontSize: '12px' }} disabled={isAuto} /></div>
            <div><div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>% Thay Đổi Min</div><input type="number" step="0.1" value={minPct} onChange={e => setMinPct(e.target.value)} style={{ ...baseStyles.input, maxWidth: '100px', fontSize: '12px' }} disabled={isAuto} /></div>
            <div><div style={{ fontSize: '11px', color: colors.textSub, marginBottom: '4px' }}>% Thay Đổi Max</div><input type="number" step="0.1" value={maxPct} onChange={e => setMaxPct(e.target.value)} style={{ ...baseStyles.input, maxWidth: '100px', fontSize: '12px' }} disabled={isAuto} /></div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}><button onClick={handleStep2Filter} disabled={isAuto} style={{ ...baseStyles.select, backgroundColor: isAuto ? colors.border : '#3b82f6', color: '#fff', border: 'none', fontWeight: 'bold' }}>🎯 LỌC DANH SÁCH</button></div>
          </div>
          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: colors.textMain, backgroundColor: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '6px' }}>
              <input type="checkbox" checked={selectedCoins.size === coinList.length && coinList.length > 0} onChange={toggleSelectAll} style={{ width: '16px', height: '16px' }} disabled={isAuto} />
              <strong>CHỌN TẤT CẢ</strong>
            </label>
            <span style={{ fontSize: '12px', color: colors.textSub }}>Đang chọn: {selectedCoins.size} / {coinList.length} Coin.</span>
          </div>
          <div style={{ maxHeight: '390px', overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: '6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: colors.cardBg, borderBottom: `2px solid ${colors.border}`, zIndex: 1 }}>
                <tr>
                  <th style={{ padding: '10px', textAlign: 'center', width: '60px' }}>Chọn</th>
                  <th style={{ padding: '10px' }}>Tên Coin</th>
                  <th style={{ padding: '10px' }}>Giá Live</th>
                  <th style={{ padding: '10px' }}>24h Thay đổi</th>
                  <th style={{ padding: '10px' }}>24h Volume</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Soi Cản</th>
                </tr>
              </thead>
              <tbody>
                {coinList.map(coin => {
                  const isChecked = selectedCoins.has(coin.symbol);
                  return (
                    <tr key={coin.symbol} style={{ borderBottom: `1px solid ${colors.border}`, backgroundColor: isChecked ? 'transparent' : 'rgba(255,255,255,0.02)', opacity: isChecked ? 1 : 0.4 }}>
                      <td style={{ padding: '10px', textAlign: 'center' }}><input type="checkbox" checked={isChecked} onChange={() => toggleCoin(coin.symbol)} disabled={isAuto} /></td>
                      <td style={{ padding: '10px', fontWeight: 'bold', color: colors.textMain }}>{coin.symbol}</td>
                      <td style={{ padding: '10px', color: colors.textMain }}>{formatSmartPrice(coin.lastPrice)}</td>
                      <td style={{ padding: '10px', color: coin.priceChange >= 0 ? colors.green : colors.red }}>{coin.priceChange >= 0 ? '+' : ''}{coin.priceChange.toFixed(2)}%</td>
                      <td style={{ padding: '10px', color: colors.textSub }}>{formatUSD(coin.quoteVolume)}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <button 
                          onClick={() => onOpenRadar && onOpenRadar(coin.symbol)}
                          style={{ background: colors.greenBg, color: colors.green, border: `1px solid ${colors.green}`, borderRadius: '4px', padding: '4px 8px', fontSize: '10px', cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          DÒ S/R
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {coinList.length > 0 && (
        <div style={{ padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ fontSize: '13px', color: colors.textMain, fontWeight: 'bold' }}>⚙️ BƯỚC 3: CẤU HÌNH BỘ NẢY CÒ MA (TRIGGER)</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select value={autoTime} onChange={(e) => setAutoTime(Number(e.target.value))} style={{ ...baseStyles.input, padding: '4px 8px', width: '80px', fontSize: '12px' }} disabled={isAuto}>
                <option value={5}>5p</option><option value={10}>10p</option><option value={15}>15p</option><option value={30}>30p</option>
              </select>
              <div style={{ display: 'flex', backgroundColor: colors.bg, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                <button onClick={() => setPendingAuto(false)} style={{ padding: '6px 12px', fontSize: '12px', border: 'none', background: !pendingAuto ? 'rgba(255,255,255,0.1)' : 'transparent', color: !pendingAuto ? '#fff' : colors.textSub, cursor: 'pointer', fontWeight: 'bold' }}>🔘 MANUAL</button>
                <button onClick={() => setPendingAuto(true)} style={{ padding: '6px 12px', fontSize: '12px', border: 'none', background: pendingAuto ? colors.greenBg : 'transparent', color: pendingAuto ? colors.green : colors.textSub, cursor: 'pointer', fontWeight: 'bold' }}>🟢 AUTO</button>
              </div>
              <button onClick={handleSaveMode} style={{ ...baseStyles.select, backgroundColor: '#3b82f6', color: '#fff', border: 'none', fontWeight: 'bold' }}>💾 LƯU CHẾ ĐỘ</button>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: colors.textSub }}>Khung thời gian soi MA:</span>
              <select value={maInterval} onChange={e=>setMaInterval(e.target.value)} style={baseStyles.select} disabled={isAuto}>
                <option value="15m">15 Phút</option><option value="30m">30 Phút</option><option value="1h">1 Giờ</option><option value="4h">4 Giờ</option><option value="1d">1 Ngày</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: colors.textSub }}>Cảnh báo Sắp Cắt (Gap %):</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: colors.textMain }}>MA7 cách MA25 &lt;</span>
                <input type="number" step="0.1" value={gapPct} onChange={e=>setGapPct(e.target.value)} style={{...baseStyles.input, width: '70px'}} disabled={isAuto} />
                <span style={{ fontSize: '12px', color: colors.textMain }}>%</span>
              </div>
            </div>
          </div>

          <div style={{ fontSize: '12px', color: colors.textSub, marginBottom: '8px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '4px' }}>1. BỘ LỌC VỊ TRÍ MA & BIẾN ĐỘNG</div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isAuto ? 'not-allowed' : 'pointer', backgroundColor: filterMABelow ? 'rgba(34, 197, 94, 0.1)' : 'rgba(255,255,255,0.05)', padding: '10px 16px', borderRadius: '6px', border: `1px dashed ${filterMABelow ? colors.green : colors.border}` }}>
              <input type="checkbox" checked={filterMABelow} onChange={handleCheckMABelow} disabled={isAuto} style={{ width: '16px', height: '16px' }}/>
              <span style={{ fontSize: '13px', color: colors.textMain }}>📉 <strong>MA7 Nằm Dưới MA25</strong></span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isAuto ? 'not-allowed' : 'pointer', backgroundColor: filterMAAbove ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255,255,255,0.05)', padding: '10px 16px', borderRadius: '6px', border: `1px dashed ${filterMAAbove ? colors.red : colors.border}` }}>
              <input type="checkbox" checked={filterMAAbove} onChange={handleCheckMAAbove} disabled={isAuto} style={{ width: '16px', height: '16px' }}/>
              <span style={{ fontSize: '13px', color: colors.textMain }}>📈 <strong>MA7 Nằm Trên MA25</strong></span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(255, 193, 7, 0.05)', borderRadius: '6px', border: `1px dashed ${enableRangeFilter ? '#ffc107' : colors.border}`, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={enableRangeFilter} onChange={e => setEnableRangeFilter(e.target.checked)} disabled={isAuto} style={{ width: '18px', height: '18px' }}/>
              <strong style={{ fontSize: '13px', color: '#ffc107' }}>KÍCH HOẠT LỌC BIẾN ĐỘNG:</strong>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
              <span>Cách đây</span>
              <input type="number" value={rangeLookback} onChange={e => setRangeLookback(e.target.value)} style={{ ...baseStyles.input, width: '60px', padding: '4px' }} disabled={isAuto || !enableRangeFilter} />
              <span>nến, biên độ (H-L) ≥</span>
              <input type="number" value={rangeThreshold} onChange={e => setRangeThreshold(e.target.value)} style={{ ...baseStyles.input, width: '60px', padding: '4px' }} disabled={isAuto || !enableRangeFilter} />
              <span>%</span>
            </div>
          </div>

          <div style={{ fontSize: '12px', color: colors.textSub, marginBottom: '8px', borderBottom: `1px solid ${colors.border}`, paddingBottom: '4px' }}>2. BỘ LỌC BẪY GIÁ (FAKEOUT)</div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isAuto ? 'not-allowed' : 'pointer', backgroundColor: filterLong ? 'rgba(34, 197, 94, 0.1)' : 'rgba(255,255,255,0.05)', padding: '10px 16px', borderRadius: '6px', border: `1px dashed ${filterLong ? colors.green : colors.border}` }}>
              <input type="checkbox" checked={filterLong} onChange={handleCheckLong} disabled={isAuto} style={{ width: '16px', height: '16px' }}/>
              <span style={{ fontSize: '13px', color: colors.textMain }}>📈 <strong>Giá Live &gt; MA7</strong></span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isAuto ? 'not-allowed' : 'pointer', backgroundColor: filterShort ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255,255,255,0.05)', padding: '10px 16px', borderRadius: '6px', border: `1px dashed ${filterShort ? colors.red : colors.border}` }}>
              <input type="checkbox" checked={filterShort} onChange={handleCheckShort} disabled={isAuto} style={{ width: '16px', height: '16px' }}/>
              <span style={{ fontSize: '13px', color: colors.textMain }}>📉 <strong>Giá Live &lt; MA7</strong></span>
            </label>
          </div>

          <button onClick={scanMACross} disabled={step3Loading || selectedCoins.size === 0 || isAuto} style={{ width: '100%', padding: '12px', fontSize: '14px', backgroundColor: (step3Loading || selectedCoins.size === 0 || isAuto) ? colors.border : '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold' }}>
            {isAuto ? `🟢 ĐANG TỰ ĐỘNG QUÉT (${activeInterval}p)` : (step3Loading ? `⏳ ĐANG QUÉT API... (${progress.current}/${progress.total})` : `🚀 BẮT ĐẦU DÒ MA CHO ${selectedCoins.size} COIN`)}
          </button>
        </div>
      )}

      {(scanResults.length > 0 || (progress.current === progress.total && progress.total > 0 && !step3Loading)) && (
        <div style={{ marginTop: '24px', borderTop: `2px dashed ${colors.border}`, paddingTop: '20px' }}>
          <div style={{ color: colors.warn, fontWeight: 'bold', marginBottom: '16px', fontSize: '16px' }}>🚨 KẾT QUẢ: MỤC TIÊU VÀO TẦM NGẮM ({scanResults.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {scanResults.map((res, idx) => {
              // Màu MA7 và MA25 dựa trên so sánh chéo
              const ma7Color = res.ma7 > res.ma25 ? colors.green : colors.red;
              const ma25Color = res.ma7 > res.ma25 ? colors.red : colors.green;

              return (
                <div key={idx} style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderLeft: `4px solid ${res.color}`, borderRadius: '0 8px 8px 0', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <strong style={{ fontSize: '16px', color: res.color }}>{idx + 1}. [ {res.symbol} ]</strong>
                      <span style={{ fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '4px' }}>Gap M7-25: {res.gap.toFixed(2)}%</span>
                      <span style={{ fontSize: '12px', fontWeight: 'bold', color: res.color }}>{res.status}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textSub, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div>• Giá Live: <strong style={{color: colors.textMain}}>{formatSmartPrice(res.livePrice)}</strong> <span style={{ color: res.change >= 0 ? colors.green : colors.red }}>({res.change >= 0 ? '+' : ''}{res.change.toFixed(2)}%)</span></div>
                      
                      {/* MA7 so sánh với Giá Live bằng % lệch (vốn có) và text màu dựa trên MA25 */}
                      <div>
                        • MA7: <strong style={{color: ma7Color}}>{formatSmartPrice(res.ma7)}</strong> 
                        {" "}| Lệch Giá: <span style={{ color: res.priceMa7Gap >= 0 ? colors.green : colors.red, fontWeight: 'bold' }}>{res.priceMa7Gap >= 0 ? '+' : ''}{res.priceMa7Gap.toFixed(2)}%</span>
                      </div>
                      
                      {/* MA25 làm hàng riêng, text màu dựa trên MA7 và thêm Lệch Giá live */}
                      <div>
                        • MA25: <strong style={{color: ma25Color}}>{formatSmartPrice(res.ma25)}</strong> 
                        {" "}| Lệch Giá: <span style={{ color: res.priceMa25Gap >= 0 ? colors.green : colors.red, fontWeight: 'bold' }}>{res.priceMa25Gap >= 0 ? '+' : ''}{res.priceMa25Gap.toFixed(2)}%</span>
                      </div>
                      
                      {/* MA99 tách riêng thành một hàng hoàn toàn */}
                      <div>
                        • MA99: <strong>{formatSmartPrice(res.ma99)}</strong> (Lệch M7-99: <span style={{ color: res.gap99 >= 0 ? colors.green : colors.red, fontWeight: 'bold' }}>{res.gap99 >= 0 ? '+' : ''}{res.gap99.toFixed(2)}%</span>)
                      </div>
                      
                      <div style={{ color: colors.warn }}>• 24h Vol: <strong>{formatUSD(res.vol)}</strong></div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button onClick={() => window.open(`https://www.binance.com/vi/futures/${res.symbol}`, '_blank')} style={{ ...baseStyles.select, border: `1px solid ${colors.border}`, fontSize: '12px', padding: '6px 12px' }}>📈 CHART</button>
                    <button onClick={() => onOpenRadar && onOpenRadar(res.symbol)} style={{ ...baseStyles.select, border: `1px solid ${colors.green}`, color: colors.green, background: colors.greenBg, fontSize: '12px', padding: '6px 12px', fontWeight: 'bold' }}>📊 DÒ CẢN S/R</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
